
import { Vue, Component, Prop, Watch, Emit } from "vue-property-decorator"
// @ts-ignore
import draggable from "vuedraggable"
import { Getter } from "vuex-class"
import { TemplateDetails, Field, FieldInstance } from "@/types"
import cloneDeep from "lodash/cloneDeep"
import VueSelect from "vue-select"
import { TenantFeatures } from "@/types/tenantFeatures"

interface KeyAndLabel {
	key: string
	label: string
}

@Component({
	components: {
		draggable,
		"v-select": VueSelect
	}
})
export default class RunningTotalsSelector extends Vue {
	@Prop({ default: undefined })
	value!: { [key: string]: string[] } | undefined

	@Prop({ required: true })
	template!: TemplateDetails

	@Getter getIsFeatureEnabled!: (feature: TenantFeatures) => boolean

	/* Constants */
	permittedFieldTypes: string[] = ["number", "calculated", "user-role"]

	/* access / set 'value' via this */
	runningTotals: { [key: string]: string[] } = {}

	/* Adding a new running total section */
	addingRunningTotalSection: boolean = false
	newSectionTitle: string = ""

	/* Renaming a section */
	editingSectionTitles: string[] = []
	sectionTitleRenameConflicts: string[] = []

	mounted () {
		if (this.value) {
			this.runningTotals = cloneDeep(this.value) ?? {}
		}
	}

	get runningTotalSectionNames (): string[] {
		return this.runningTotals ? Object.keys(this.runningTotals) : []
	}

	async addSection () {
		this.newSectionTitle = this.newSectionTitle.trim()

		if (this.newSectionTitle === "" ||
			this.runningTotalSectionNames.some(sectionName => this.newSectionTitle.toLowerCase() === sectionName.toLowerCase())) {
			return
		}

		Vue.set(this.runningTotals!, this.newSectionTitle, [])

		this.newSectionTitle = ""
		this.addingRunningTotalSection = false
	}

	cancelAddSection (): void {
		this.newSectionTitle = ""
		this.addingRunningTotalSection = false
	}

	get sectionCreationValidationWarnings (): string {
		if (this.addingRunningTotalSection) {
			const newSectionTitle = this.newSectionTitle.trim().toLowerCase()
			if (newSectionTitle === "") {
				return "Section title cannot be blank"
			} else if (this.runningTotalSectionNames.some(sectionName => sectionName.toLowerCase() === newSectionTitle.toLowerCase()) &&
					!this.editingSectionTitles.includes(newSectionTitle)) {
				return "Section title already exists"
			}
		}

		return ""
	}

	get validationWarnings (): string {
		if (this.sectionCreationValidationWarnings.length > 0) {
			return this.sectionCreationValidationWarnings
		}

		if (this.runningTotalSectionNames.some(section => this.runningTotals[section]?.length === 0)) {
			return "All running total sections must contain at least one field"
		}

		return ""
	}

	@Watch("validationWarnings", { immediate: true })
	validationChanged (): void {
		this.$emit("validation-changed", this.validationWarnings)
	}

	@Watch("runningTotals", { immediate: true, deep: true })
	valueChanged (newValue: { [key: string]: string[]}): void {
		if (newValue === {}) {
			this.$emit("input", undefined)
		} else {
			this.$emit("input", newValue)
		}
	}

	onEnterPressed () {
		if (this.addingRunningTotalSection && this.sectionCreationValidationWarnings.length === 0) {
			this.addSection()
		}
	}

	get fieldOptions (): KeyAndLabel[] {
		return this.template.fields.filter(field => field.field.dataType && this.permittedFieldTypes.includes(field.field.dataType))
			.map(field => {
				return {
					key: field.field.id!,
					label: field.field.title
				}
			})
	}

	@Watch("fieldOptions")
	templateFieldOptionsChanged (newFields: KeyAndLabel[], oldFields: KeyAndLabel[]): void {
		const existingTemplateFieldIds = oldFields.map(field => field.key)
		const updatedTemplateFieldIds = newFields.map(field => field.key)

		const fieldsWereRemoved = !existingTemplateFieldIds.every(fieldId => updatedTemplateFieldIds.includes(fieldId))

		if (!fieldsWereRemoved) {
			return
		}

		let updatedRunningTotals = cloneDeep(this.runningTotals)

		// remove any instances of the removed field from all sections
		Object.keys(updatedRunningTotals).forEach(sectionName => {
			updatedRunningTotals[sectionName] = updatedRunningTotals[sectionName].filter(fieldId => updatedTemplateFieldIds.includes(fieldId))
		})

		this.runningTotals = updatedRunningTotals
	}

	attemptSectionRename (newName: string, existingName: string) {
		newName = newName.trim()

		if (newName === "" || newName.toLowerCase() === existingName.toLowerCase()) {
			this.cancelSectionTitleEdit(existingName)
		} else if (this.runningTotalSectionNames.some(sectionName => newName.toLowerCase() === sectionName.toLowerCase())) {
			this.addSectionTitleRenameConflict(existingName)
		} else {
			Vue.set(this.runningTotals, newName, this.runningTotals[existingName])
			this.$delete(this.runningTotals, existingName)
			this.editingSectionTitles.remove(existingName)
			this.sectionTitleRenameConflicts.remove(existingName)
		}
	}

	checkForNameConflicts (newName: string, existingName: string) {
		newName = newName.trim().toLowerCase()
		if (this.runningTotalSectionNames.some(sectionName => newName === sectionName.toLowerCase()) &&
				newName !== existingName.toLowerCase()) {
			this.addSectionTitleRenameConflict(existingName)
		} else {
			this.sectionTitleRenameConflicts.remove(existingName)
		}
	}

	cancelSectionTitleEdit (sectionName: string): void {
		this.editingSectionTitles.remove(sectionName)
		this.sectionTitleRenameConflicts.remove(sectionName)
	}

	addSectionTitleRenameConflict (sectionName: string): void {
		if (!this.sectionTitleRenameConflicts.includes(sectionName)) {
			this.sectionTitleRenameConflicts.push(sectionName)
		}
	}

	async deleteSection (sectionName: string): Promise<void> {
		this.cancelSectionTitleEdit(sectionName)
		Vue.delete(this.runningTotals, sectionName)
		await this.$nextTick()
		this.refreshSectionTitleRenameConflicts()
	}

	async showSectionTitleEdit (sectionName: string): Promise<void> {
		if (!this.editingSectionTitles.includes(sectionName)) {
			this.editingSectionTitles.push(sectionName)
		}

		await this.$nextTick()

		const titleInput = document.getElementById(`section-title-rename-input-${sectionName}`)
		if (titleInput) {
			titleInput.focus()
		}
	}

	refreshSectionTitleRenameConflicts (): void {
		this.sectionTitleRenameConflicts.forEach(sectionName => {
			const titleInput = document.getElementById(`section-title-rename-input-${sectionName}`)
			if (!titleInput) {
				return
			}
			const desiredName = (titleInput as HTMLInputElement).value.trim()
			if (!this.runningTotalSectionNames.some(sectionName => desiredName.toLowerCase() === sectionName.toLowerCase())) {
				this.sectionTitleRenameConflicts.remove(sectionName)
				this.attemptSectionRename(desiredName, sectionName)
			}
		})
	}

	async showNewSectionCreation (): Promise<void> {
		this.addingRunningTotalSection = true
		await this.$nextTick()
		const titleInput = document.getElementById("cadence-running-total-new-section-input")
		if (titleInput) {
			titleInput.focus()
		}
	}

	private get closeOnSelect ():boolean {
		return !(this.getIsFeatureEnabled(TenantFeatures.MultiSelectKeepOpen))
	}
}

